import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { ClientHelper } from '../client/CreateNswagClient';
import type { SignalRConnectionInfo } from '../client/SmartAssistantBackendClient';

export const createSignalRConnector = (params: {
  connectionsRef: React.MutableRefObject<Map<string, any>>;
  handleIncoming: (chatKey: string, payload: any, eventName?: string) => void;
}) => {
  const { connectionsRef, handleIncoming } = params;

  return async (consumer: any, chatKey: string) => {
    if (!chatKey) return;
    const existing = connectionsRef.current.get(chatKey);
    if (existing && (existing.state === HubConnectionState.Connected || existing.state === HubConnectionState.Connecting)) {
      return;
    }

    try {
      const token = await consumer.getUserToken();
      const signalRClient = ClientHelper.createSignalRClient(token as string);
      const connectionInfo: SignalRConnectionInfo = await signalRClient.negotiate(chatKey);

      const connection = new HubConnectionBuilder()
        .withUrl(connectionInfo.url ?? '', {
          accessTokenFactory: () => connectionInfo.accessToken ?? '',
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000, 300000])
        .configureLogging(LogLevel.Information)
        .build();
      connection.keepAliveIntervalInMilliseconds = 10_000;
      connection.serverTimeoutInMilliseconds = 120_000;

      const wireHandler = (eventName: string) => (msg: any) => {
        let seqValue: number | undefined;
        try {
          const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
          const rawSeq = parsed?.seq ?? parsed?.Seq;
          const parsedSeq = typeof rawSeq === 'string' ? Number(rawSeq) : rawSeq;
          seqValue = typeof parsedSeq === 'number' && !Number.isNaN(parsedSeq) ? parsedSeq : undefined;
          console.log('[SignalR] raw message', { eventName, chatId: chatKey, seq: seqValue, msg: parsed });
        } catch {
          console.log('[SignalR] raw message', { eventName, chatId: chatKey, seq: undefined, msg });
        }
        handleIncoming(chatKey, msg, eventName);
      };
      connection.on('partial', wireHandler('partial'));
      connection.on('partialResponse', wireHandler('partialResponse'));
      connection.on('assistantResponse', wireHandler('assistantResponse'));
      connection.on('chatUpdate', wireHandler('chatUpdate'));
      connection.on('message', wireHandler('message'));

      connection.onclose((err) => {
        if (err) {
          console.warn('SignalR closed with error', err);
        }
        connectionsRef.current.delete(chatKey);
      });

      await connection.start();
      connectionsRef.current.set(chatKey, connection);
    } catch (err) {
      console.error('SignalR connect failed', err);
    }
  };
};
